import { t as uiText } from '../i18n';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useWorkspaceContext } from '../context/WorkspaceContext';
import { useAppUpdate } from '../hooks/useAppUpdate';
import { resolveWorkspacePalette } from '../theme/workspaceBaseColors';
import { UPDATE_REQUIRED_EVENT } from '../utils/chunkReload';
import { shouldShowDevChrome, shouldSuppressUpdateBanner } from '../utils/embedContext';
import WidgetErrorView from './widget/WidgetErrorView';

const fontStack =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Hiragino Sans", "Noto Sans JP", sans-serif';

const WIDGET_ERROR_CODES = [
  'ORIGIN_NOT_ALLOWED',
  'TOKEN_INVALID',
  'TOKEN_DISABLED',
  'PUBLISH_ID_NOT_FOUND',
  'WORKSPACE_MISMATCH',
  'MISSING_PARAMS',
  'RATE_LIMITED',
  'VALIDATE_INTERNAL_ERROR',
  'UNKNOWN',
] as const;

type WidgetErrorCode = typeof WIDGET_ERROR_CODES[number];

type UpdateNotice = 'modest' | 'required';

export default function UpdateBanner() {
  const { pathname } = useLocation();
  const { selectedWorkspace } = useWorkspaceContext();
  const updateAvailable = useAppUpdate(pathname);
  const palette = selectedWorkspace?.baseColorKey
    ? resolveWorkspacePalette(selectedWorkspace.baseColorKey)
    : null;
  const primary = palette?.primary ?? 'var(--workspace-primary, #cfae91)';
  const primaryDark = palette?.primaryDark ?? 'var(--workspace-primary-dark, #b89a7a)';
  const bgSoft = palette?.bgSoft ?? 'var(--workspace-bg-soft, #E9D8C9)';
  const textOnPrimary = palette?.textOnPrimary ?? 'var(--workspace-text-on-primary, #fff)';
  const [notice, setNotice] = useState<UpdateNotice | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [btnHover, setBtnHover] = useState(false);
  const [laterHover, setLaterHover] = useState(false);
  const [fabHover, setFabHover] = useState(false);
  const [accessDeniedFabHover, setAccessDeniedFabHover] = useState(false);
  const [widgetErrorFabHover, setWidgetErrorFabHover] = useState(false);
  const [widgetErrorPreviewCode, setWidgetErrorPreviewCode] = useState<WidgetErrorCode | null>(null);

  useEffect(() => {
    if (shouldSuppressUpdateBanner()) return;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('show-update-banner') === 'true') {
      setNotice((prev) => prev ?? 'modest');
    }

    const handleUpdateRequired = () => {
      if (shouldSuppressUpdateBanner()) return;
      setNotice('required');
    };
    window.addEventListener(UPDATE_REQUIRED_EVENT, handleUpdateRequired);
    return () => {
      window.removeEventListener(UPDATE_REQUIRED_EVENT, handleUpdateRequired);
    };
  }, [pathname]);

  useEffect(() => {
    if (!updateAvailable || dismissed) return;
    setNotice((prev) => prev ?? 'modest');
  }, [updateAvailable, dismissed]);

  // 公開埋め込み面では描画しない（show=true でも render しない）
  if (shouldSuppressUpdateBanner()) return null;

  const handleUpdate = () => {
    window.location.reload();
  };

  const handleLater = () => {
    setNotice(null);
    setDismissed(true);
  };

  const show = notice !== null;
  const required = notice === 'required';

  const isDev = import.meta.env.DEV;

  return (
    <>
      {show && (
        <div
          role="dialog"
          aria-modal="false"
          aria-labelledby="update-notice-title"
          style={{
            position: 'fixed',
            left: 16,
            bottom: 'max(20px, env(safe-area-inset-bottom, 0px))',
            zIndex: 99999,
            width: 'min(360px, calc(100vw - 32px))',
            padding: '16px 16px 14px',
            fontFamily: fontStack,
            background: '#fff',
            borderRadius: 16,
            border: '1px solid var(--workspace-border-soft, rgba(207, 174, 145, 0.35))',
            boxShadow: '0 12px 32px -8px rgba(40, 32, 24, 0.28)',
            animation: 'updateNoticeRise 0.28s ease-out',
          }}
        >
          <style>
            {`
          @keyframes updateNoticeRise {
            from {
              transform: translateY(12px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
          </style>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div
              style={{
                flexShrink: 0,
                width: 40,
                height: 40,
                borderRadius: 12,
                background: bgSoft,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: primary,
              }}
              aria-hidden
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M23 4v6h-6" />
                <path d="M1 20v-6h6" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </div>
            <div style={{ textAlign: 'left', minWidth: 0, paddingTop: 1 }}>
              <strong
                id="update-notice-title"
                style={{
                  display: 'block',
                  fontSize: '0.98rem',
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  color: '#3d3429',
                  lineHeight: 1.35,
                }}
              >
                {required
                  ? uiText("notreu has been updated.")
                  : uiText("New update available")}
              </strong>
              <p
                style={{
                  margin: '6px 0 0',
                  fontSize: '0.8125rem',
                  fontWeight: 400,
                  color: '#6b5d52',
                  lineHeight: 1.6,
                }}
              >
                {required
                  ? uiText("Reload the page to use the latest version.")
                  : uiText("Refresh the page to pick up the latest features and fixes.")}
              </p>
              <p
                style={{
                  margin: '6px 0 0',
                  fontSize: '0.8125rem',
                  fontWeight: 400,
                  color: '#6b5d52',
                  lineHeight: 1.6,
                }}
              >
                {uiText("If you have unsaved input, save it before updating.")}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button
              type="button"
              onClick={handleLater}
              onMouseEnter={() => setLaterHover(true)}
              onMouseLeave={() => setLaterHover(false)}
              style={{
                flex: 1,
                padding: '10px 16px',
                backgroundColor: laterHover ? 'var(--workspace-bg-soft, #f5f1ed)' : '#fff',
                color: '#3d3429',
                border: '1px solid var(--workspace-border-soft, rgba(207, 174, 145, 0.55))',
                borderRadius: 9999,
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '0.9375rem',
                letterSpacing: '0.01em',
                fontFamily: fontStack,
                transition: 'background-color 0.2s ease',
              }}
            >
              {uiText("Later")}
            </button>
            <button
              type="button"
              onClick={handleUpdate}
              onMouseEnter={() => setBtnHover(true)}
              onMouseLeave={() => setBtnHover(false)}
              style={{
                flex: 1,
                padding: '10px 16px',
                backgroundColor: btnHover ? primaryDark : primary,
                color: textOnPrimary,
                border: 'none',
                borderRadius: 9999,
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '0.9375rem',
                letterSpacing: '0.01em',
                fontFamily: fontStack,
                transition: 'background-color 0.2s ease, transform 0.15s ease',
                transform: btnHover ? 'translateY(-1px)' : 'translateY(0)',
              }}
            >
              {required ? uiText("Reload") : uiText("Update now")}
            </button>
          </div>
        </div>
      )}

      {/* 公開ウィジェットのエラー画面プレビュー（dev/管理者向け） */}
      {isDev && widgetErrorPreviewCode && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.28)',
            zIndex: 99998,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            animation: 'widgetErrorPreviewFade 0.18s ease-out',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setWidgetErrorPreviewCode(null);
          }}
        >
          <style>
            {`
              @keyframes widgetErrorPreviewFade {
                from { opacity: 0; }
                to { opacity: 1; }
              }
            `}
          </style>
          <div
            style={{
              position: 'relative',
              width: 420,
              maxWidth: 'calc(100vw - 32px)',
              height: 420,
              maxHeight: 'calc(100vh - 100px)',
              background: '#ffffff',
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 12,
                left: 12,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: 'rgba(255, 255, 255, 0.92)',
                backdropFilter: 'blur(8px)',
                padding: '4px 6px 4px 10px',
                borderRadius: 999,
                border: '1px solid rgba(207, 174, 145, 0.35)',
                boxShadow: '0 2px 8px rgba(90, 70, 55, 0.08)',
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  color: '#8b6f52',
                  textTransform: 'uppercase',
                  fontFamily: fontStack,
                }}
              >
                Preview
              </span>
              <select
                value={widgetErrorPreviewCode}
                onChange={(e) => setWidgetErrorPreviewCode(e.target.value as WidgetErrorCode)}
                style={{
                  fontSize: 11,
                  padding: '3px 6px',
                  border: '1px solid rgba(207, 174, 145, 0.4)',
                  borderRadius: 6,
                  background: '#fff',
                  color: '#5c4a3a',
                  fontFamily: fontStack,
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                {WIDGET_ERROR_CODES.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={() => setWidgetErrorPreviewCode(null)}
              aria-label={uiText("Close preview")}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 2,
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.92)',
                backdropFilter: 'blur(8px)',
                color: '#5c4a3a',
                border: '1px solid rgba(207, 174, 145, 0.35)',
                boxShadow: '0 2px 8px rgba(90, 70, 55, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                lineHeight: 1,
                cursor: 'pointer',
                fontFamily: fontStack,
              }}
            >
              ×
            </button>

            <div style={{ flex: 1, overflow: 'auto', display: 'flex' }}>
              <WidgetErrorView errorMode={widgetErrorPreviewCode} errorId="preview-xxxxxxxx" />
            </div>
          </div>
        </div>
      )}

      {shouldShowDevChrome() && (
        <>
          {/* ウィジェットエラー画面プレビューFAB */}
          <button
            type="button"
            onClick={() =>
              setWidgetErrorPreviewCode((prev) => (prev ? null : 'ORIGIN_NOT_ALLOWED'))
            }
            onMouseEnter={() => setWidgetErrorFabHover(true)}
            onMouseLeave={() => setWidgetErrorFabHover(false)}
            aria-label={uiText("Preview the widget error screen (for development)")}
            title={
              widgetErrorPreviewCode
                ? uiText("Close the widget error screen (development only)")
                : uiText("Preview the widget error screen (development only)")
            }
            style={{
              position: 'fixed',
              right: 360,
              bottom: 'max(20px, env(safe-area-inset-bottom, 0px))',
              zIndex: 99998,
              width: 56,
              height: 56,
              borderRadius: '50%',
              border: '1px solid rgba(207, 174, 145, 0.45)',
              background: widgetErrorFabHover
                ? 'linear-gradient(145deg, #ebd9c8 0%, #cfae91 100%)'
                : 'linear-gradient(145deg, #f5f0eb 0%, rgba(207, 174, 145, 0.55) 100%)',
              boxShadow: widgetErrorFabHover
                ? '0 6px 20px rgba(90, 70, 55, 0.18), 0 2px 8px rgba(139, 111, 78, 0.15)'
                : '0 4px 16px rgba(90, 70, 55, 0.12), 0 2px 6px rgba(139, 111, 78, 0.1)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#5c4a3a',
              transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
              transform: widgetErrorFabHover ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)',
              fontFamily: fontStack,
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </button>

          {/* アクセス拒否画面プレビューFAB */}
          <button
            type="button"
            onClick={() => {
              const isOnPage = window.location.pathname === '/sys/access-denied';
              if (isOnPage) {
                if (window.history.length > 1) {
                  window.history.back();
                } else {
                  window.location.href = '/';
                }
              } else {
                window.location.href = '/sys/access-denied';
              }
            }}
            onMouseEnter={() => setAccessDeniedFabHover(true)}
            onMouseLeave={() => setAccessDeniedFabHover(false)}
            aria-label={uiText("Toggle the access denied screen (for development)")}
            title={
              window.location.pathname === '/sys/access-denied'
                ? uiText("Close the access denied screen (development only)")
                : uiText("Preview the access denied screen (development only)")
            }
            style={{
              position: 'fixed',
              right: 224,
              bottom: 'max(20px, env(safe-area-inset-bottom, 0px))',
              zIndex: 99998,
              width: 56,
              height: 56,
              borderRadius: '50%',
              border: '1px solid rgba(207, 174, 145, 0.45)',
              background: accessDeniedFabHover
                ? 'linear-gradient(145deg, #ebd9c8 0%, #cfae91 100%)'
                : 'linear-gradient(145deg, #f5f0eb 0%, rgba(207, 174, 145, 0.55) 100%)',
              boxShadow: accessDeniedFabHover
                ? '0 6px 20px rgba(90, 70, 55, 0.18), 0 2px 8px rgba(139, 111, 78, 0.15)'
                : '0 4px 16px rgba(90, 70, 55, 0.12), 0 2px 6px rgba(139, 111, 78, 0.1)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#5c4a3a',
              transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
              transform: accessDeniedFabHover ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)',
              fontFamily: fontStack,
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <rect x="5" y="11" width="14" height="10" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </button>

          {/* 更新バナートグルFAB */}
          <button
            type="button"
            onClick={() => setNotice((prev) => (prev ? null : 'modest'))}
            onMouseEnter={() => setFabHover(true)}
            onMouseLeave={() => setFabHover(false)}
            aria-label={uiText("Toggle the update notification banner (for development)")}
            title={uiText("Toggle the update banner (development only)")}
            style={{
              position: 'fixed',
              right: 88,
              bottom: 'max(20px, env(safe-area-inset-bottom, 0px))',
              zIndex: 99998,
              width: 56,
              height: 56,
              borderRadius: '50%',
              border: '1px solid rgba(207, 174, 145, 0.45)',
              background: fabHover
                ? 'linear-gradient(145deg, #ebd9c8 0%, #cfae91 100%)'
                : 'linear-gradient(145deg, #f5f0eb 0%, rgba(207, 174, 145, 0.55) 100%)',
              boxShadow: fabHover
                ? '0 6px 20px rgba(90, 70, 55, 0.18), 0 2px 8px rgba(139, 111, 78, 0.15)'
                : '0 4px 16px rgba(90, 70, 55, 0.12), 0 2px 6px rgba(139, 111, 78, 0.1)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#5c4a3a',
              transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
              transform: fabHover ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)',
              fontFamily: fontStack,
            }}
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M23 4v6h-6" />
              <path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
        </>
      )}
    </>
  );
}
